import { ChangeDetectionStrategy, Component, inject, input, output, viewChild } from '@angular/core';
import { AbstractControl, FormBuilder, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { SeFeDatepickerMessages, SeFeExcludedDates } from 'se-fe-datepicker';
import { SeFeFormService } from 'se-fe-form-service';
import { SeFeMenuComponent, SeFeMenuOptions } from 'se-fe-menu';

@Component({
  selector: 'app-date-filter-menu',
  standalone: false,
  templateUrl: './date-filter-menu.component.html',
  styleUrls: ['./date-filter-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateFilterMenuComponent {
  readonly disabled = input(false);
  readonly endDate = input<string | null>(null);
  readonly filterApplied = output<DateFilterSelection>();
  readonly filterCleared = output<void>();
  readonly fiscalEndDate = input<Date | null>(null);
  readonly fiscalStartDate = input<Date | null>(null);
  readonly selectedFilter = input<DateFilterValue | null>(null);
  readonly startDate = input<string | null>(null);

  readonly menu = viewChild.required('dateMenu', { read: SeFeMenuComponent });

  private readonly formBuilder = inject(FormBuilder);
  private readonly formService = inject(SeFeFormService);
  private readonly translateService = inject(TranslateService);

  readonly discountDateFilter = 'discount-date-filter';
  readonly dateMenuOptions: SeFeMenuOptions = {
    name: this.discountDateFilter,
    title: this.translateService.instant('DISCOUNTS.LIST_VIEW.FILTERS.date'),
    cardLabel: this.translateService.instant('DISCOUNTS.LIST_VIEW.FILTERS.date_filter'),
    minWidth: '350px',
    actions: [
      {
        label: this.translateService.instant('DISCOUNTS.LIST_VIEW.FILTERS.apply'),
        action: () => this.applyFilter(),
      },
      {
        label: this.translateService.instant('DISCOUNTS.LIST_VIEW.FILTERS.clear'),
        action: () => this.clearFilter(),
      },
    ],
  };
  readonly datepickerLimit: SeFeExcludedDates[] = [{ start: DateTime.now().plus({ day: 1 }).toISODate()! }];
  readonly radioOptions: DateFilterOption[] = [
    { label: this.translateService.instant('DISCOUNTS.LIST_VIEW.FILTERS.fiscal_year'), value: 'fiscal-year' },
    { label: this.translateService.instant('DISCOUNTS.LIST_VIEW.FILTERS.current_year'), value: 'current-year' },
    { label: this.translateService.instant('DISCOUNTS.LIST_VIEW.FILTERS.last_year'), value: 'last-year' },
    { label: this.translateService.instant('DISCOUNTS.LIST_VIEW.FILTERS.current_month'), value: 'current-month' },
    { label: this.translateService.instant('DISCOUNTS.LIST_VIEW.FILTERS.last_month'), value: 'last-month' },
    {
      label: this.translateService.instant('DISCOUNTS.LIST_VIEW.FILTERS.specific_date_range'),
      value: 'date-range',
    },
  ];
  readonly datepickerMessages: SeFeDatepickerMessages = {
    dateInvalid: this.translateService.instant('DISCOUNTS.LIST_VIEW.FILTERS.ERRORS.date_invalid'),
    dateOutOfRange: this.translateService.instant('DISCOUNTS.LIST_VIEW.FILTERS.ERRORS.date_out_of_range'),
    required: this.translateService.instant('DISCOUNTS.LIST_VIEW.FILTERS.ERRORS.required'),
  };
  readonly errorMessages = {
    dateRangeInvalid: this.translateService.instant('DISCOUNTS.LIST_VIEW.FILTERS.ERRORS.date_range_invalid'),
  };
  readonly form = this.formBuilder.group(
    {
      quickFilter: this.formBuilder.control<DateFilterValue | null>(null),
      startDate: this.formBuilder.nonNullable.control('', Validators.required),
      endDate: this.formBuilder.nonNullable.control('', Validators.required),
    },
    { validators: this.dateRangeValidator() }
  );

  get errors(): string[] {
    return this.form.errors ? Object.keys(this.form.errors) : [];
  }

  onMenuOpened(): void {
    this.form.reset({
      quickFilter: this.selectedFilter(),
      startDate: this.toDisplayDate(this.startDate()),
      endDate: this.toDisplayDate(this.endDate()),
    });
  }

  applyFilter(): void {
    const selection = this.selectionFromForm();
    if (!selection) {
      return;
    }

    this.menu().closeMenu();
    this.filterApplied.emit(selection);
  }

  clearFilter(): void {
    this.form.reset({ quickFilter: null, startDate: '', endDate: '' });
    this.menu().closeMenu();
    this.filterCleared.emit();
  }

  private selectionFromForm(): DateFilterSelection | null {
    const { quickFilter, startDate, endDate } = this.form.getRawValue();

    if (quickFilter === 'date-range') {
      if (!this.formService.validate(this.form)) {
        return null;
      }

      return {
        filter: 'date-range',
        startDate: this.toIsoDate(startDate),
        endDate: this.toIsoDate(endDate),
      };
    }

    const range = this.calculateDateRange(quickFilter);
    if (!range || !quickFilter) {
      return null;
    }

    const presetStartDate = range.startDate.toISODate();
    const presetEndDate = range.endDate.toISODate();
    if (!presetStartDate || !presetEndDate) {
      return null;
    }

    return {
      filter: quickFilter,
      startDate: presetStartDate,
      endDate: presetEndDate,
    };
  }

  private calculateDateRange(filter: DateFilterValue | null): { startDate: DateTime; endDate: DateTime } | null {
    const today = DateTime.now();

    switch (filter) {
      case 'fiscal-year': {
        const fiscalStartDate = this.fiscalStartDate();
        const fiscalEndDate = this.fiscalEndDate();
        if (!fiscalStartDate || !fiscalEndDate) {
          return null;
        }
        return {
          startDate: DateTime.fromJSDate(fiscalStartDate),
          endDate: DateTime.fromJSDate(fiscalEndDate),
        };
      }
      case 'current-year':
        return { startDate: today.startOf('year'), endDate: today.endOf('year') };
      case 'last-year':
        return { startDate: today.minus({ year: 1 }).startOf('year'), endDate: today.minus({ year: 1 }).endOf('year') };
      case 'current-month':
        return { startDate: today.startOf('month'), endDate: today.endOf('month') };
      case 'last-month':
        return {
          startDate: today.minus({ month: 1 }).startOf('month'),
          endDate: today.minus({ month: 1 }).endOf('month'),
        };
      default:
        return null;
    }
  }

  private toDisplayDate(value: string | null): string {
    return value ? DateTime.fromISO(value).toFormat('MM/dd/yyyy') : '';
  }

  private toIsoDate(value: string): string {
    return DateTime.fromFormat(value, 'MM/dd/yyyy').toISODate()!;
  }

  private dateRangeValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const startDate = DateTime.fromFormat(group.get('startDate')?.value, 'MM/dd/yyyy');
      const endDate = DateTime.fromFormat(group.get('endDate')?.value, 'MM/dd/yyyy');
      return startDate.isValid && endDate.isValid && startDate > endDate ? { dateRangeInvalid: true } : null;
    };
  }
}

export interface DateFilterSelection {
  filter: DateFilterValue;
  startDate: string;
  endDate: string;
}

export type DateFilterValue =
  'fiscal-year' | 'current-year' | 'last-year' | 'current-month' | 'last-month' | 'date-range';

interface DateFilterOption {
  label: string;
  value: DateFilterValue;
}
